import { AdminTabs } from 'components/AdminTabs'
import { Icon } from 'components/Icon'
import { Card } from 'components/ui'
import { ROLE_LABELS } from 'lib/format'
import { t } from 'lib/i18n'

type Cell = 'yes' | 'no' | 'conditional'

interface Row {
  labelAr: string
  labelEn: string
  hintAr: string
  hintEn: string
  cells: Record<'engineer' | 'manager' | 'supervision' | 'admin', Cell>
}

const GROUPS: { titleAr: string; titleEn: string; rows: Row[] }[] = [
  {
    rows: [
      {
        cells: { admin: 'yes', engineer: 'yes', manager: 'yes', supervision: 'conditional' },
        hintAr: 'الإشراف يرى فقط التقارير المعتمدة',
        hintEn: 'Supervisor sees approved reports only',
        labelAr: 'عرض التقارير',
        labelEn: 'View reports',
      },
      {
        cells: { admin: 'yes', engineer: 'yes', manager: 'yes', supervision: 'no' },
        hintAr: 'طالما لم يُعتمد التقرير بعد',
        hintEn: 'As long as the report is not approved yet',
        labelAr: 'تعديل / إكمال مسودة',
        labelEn: 'Edit or complete a draft',
      },
      {
        cells: { admin: 'yes', engineer: 'yes', manager: 'yes', supervision: 'no' },
        hintAr: 'قبل أن يُرسل التقرير للاعتماد',
        hintEn: 'Before the report is sent for approval',
        labelAr: 'إرسال للاعتماد',
        labelEn: 'Submit for approval',
      },
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'yes', supervision: 'no' },
        hintAr: 'يقفل التقرير عن التعديل',
        hintEn: 'Locks the report so it cannot be edited',
        labelAr: 'اعتماد',
        labelEn: 'Approve',
      },
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'yes', supervision: 'no' },
        hintAr: 'يرسله للمراجعة مع ملاحظة',
        hintEn: 'Sends it for review, with a note',
        labelAr: 'إرسال للمراجعة',
        labelEn: 'Send for review',
      },
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'yes', supervision: 'no' },
        hintAr: 'بعد الاعتماد، مع ملاحظة',
        hintEn: 'After approval, with a note',
        labelAr: 'إعادة فتح تقرير معتمد',
        labelEn: 'Reopen an approved report',
      },
      {
        cells: { admin: 'yes', engineer: 'yes', manager: 'yes', supervision: 'conditional' },
        hintAr: 'الإشراف فقط على التقارير المعتمدة؛ غيرهم أثناء التعديل',
        hintEn: 'Supervisor only on approved reports; others while the report can be edited',
        labelAr: 'إضافة ملاحظة إشراف',
        labelEn: 'Add a supervision note',
      },
      {
        cells: { admin: 'yes', engineer: 'yes', manager: 'yes', supervision: 'conditional' },
        hintAr: 'لأي تقرير يمكنهم عرضه',
        hintEn: 'For any report they can view',
        labelAr: 'تنزيل Excel / PDF',
        labelEn: 'Download Excel / PDF',
      },
    ],
    titleAr: 'التقارير اليومية',
    titleEn: 'Daily reports',
  },
  {
    rows: [
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'no', supervision: 'no' },
        hintAr: 'إنشاء مشروع جديد وتعديل بياناته',
        hintEn: 'Create a new project and edit its details',
        labelAr: 'إدارة المشاريع',
        labelEn: 'Manage projects',
      },
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'yes', supervision: 'no' },
        hintAr: 'الاختصاصات، المعدات، المواد',
        hintEn: 'Trades, equipment, and materials',
        labelAr: 'إدارة القوائم المرجعية',
        labelEn: 'Manage reference lists',
      },
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'no', supervision: 'no' },
        hintAr: 'تعيين الأدوار، الإزالة، الدعوات',
        hintEn: 'Assign roles, remove members, and send invitations',
        labelAr: 'إدارة أعضاء المشروع والدعوات',
        labelEn: 'Manage project members and invitations',
      },
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'no', supervision: 'no' },
        hintAr: 'عبر كل المشاريع',
        hintEn: 'Across every project',
        labelAr: 'إدارة المستخدمين',
        labelEn: 'Manage users',
      },
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'no', supervision: 'no' },
        hintAr: 'رفع أو استعادة قالب Excel',
        hintEn: 'Upload or restore an Excel template',
        labelAr: 'إدارة قالب التقرير',
        labelEn: 'Manage the report template',
      },
      {
        cells: { admin: 'yes', engineer: 'no', manager: 'yes', supervision: 'no' },
        hintAr: 'بيانات المشروع، القوائم، الأعضاء',
        hintEn: 'Project details, lists, and members',
        labelAr: 'الوصول إلى إعدادات المشروع',
        labelEn: 'Access project settings',
      },
    ],
    titleAr: 'الإدارة',
    titleEn: 'Administration',
  },
]

const COLUMNS: { key: keyof Row['cells']; role: 'SITE_ENGINEER' | 'PROJECT_MANAGER' | 'ADMIN' | 'SUPERVISOR' }[] = [
  { key: 'engineer', role: 'SITE_ENGINEER' },
  { key: 'manager', role: 'PROJECT_MANAGER' },
  { key: 'admin', role: 'ADMIN' },
  { key: 'supervision', role: 'SUPERVISOR' },
]

const CellIcon = ({ value }: { value: Cell }) => {
  if (value === 'yes') return <Icon name="check" size={16} className="roles-cell roles-cell--yes" />
  if (value === 'conditional') return <Icon name="info" size={16} className="roles-cell roles-cell--conditional" />
  return <span className="roles-cell roles-cell--no">—</span>
}

/** Read-only reference for the system's fixed role permissions (Figma: Admin / 22 · Roles & permissions).
 *  The matrix in the mockup is editable with a 5th "Client" role — neither exists in the backend today
 *  (permissions are hardcoded in reportPermissions(), and ProjectRole has no CLIENT value). Rather than
 *  fake an editor that doesn't do anything, this mirrors the real, fixed logic so people can see it. */
export const RolesPage = () => (
  <div className="page">
    <div className="page__head">
      <div>
        <h1>{t('المستخدمون والأدوار', 'Users and roles')}</h1>
        <p className="muted">
          {t('ما يستطيع كل دور فعله — هذه الصلاحيات ثابتة في النظام حالياً ولا يمكن تخصيصها', 'What each role can do. These permissions are fixed in the system and cannot be customized.')}
        </p>
      </div>
    </div>

    <AdminTabs />

    {GROUPS.map((group) => (
      <Card title={t(group.titleAr, group.titleEn)} key={group.titleAr}>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('الصلاحية', 'Permission')}</th>
                {COLUMNS.map((column) => (
                  <th scope="col" key={column.key}>
                    {ROLE_LABELS[column.role]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.rows.map((row) => (
                <tr key={row.labelAr}>
                  <td>
                    <strong>{t(row.labelAr, row.labelEn)}</strong>
                    <p className="muted small">{t(row.hintAr, row.hintEn)}</p>
                  </td>
                  {COLUMNS.map((column) => (
                    <td key={column.key}>
                      <CellIcon value={row.cells[column.key]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    ))}
  </div>
)
